import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Mail, Calendar, Linkedin } from 'lucide-react';
import Reveal from '../components/Reveal';
import { values, engagements, images, bookingUrl, founderLinkedin } from '../data/mock';

const HeroSection = () => (
  <section className="relative pt-40 pb-24 bg-[#010c23] text-white overflow-hidden">
    <div className="absolute inset-0 opacity-30">
      <img src={images.office2} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#010c23]/70 to-[#010c23]" />
    </div>
    <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
      <Reveal>
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-14 bg-[#ff7300]" />
          <span className="text-[11px] tracking-[0.35em] uppercase font-medium text-[#ff7300]">Le cabinet</span>
        </div>
        <h1 className="font-serif text-5xl md:text-7xl font-light leading-tight max-w-4xl">
          Un conseil patrimonial <span className="italic text-[#ff7300]">clair, indépendant</span> et engagé
        </h1>
      </Reveal>
    </div>
  </section>
);

const FounderSection = () => (
  <section className="py-20 bg-[#fbf9f5]">
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid md:grid-cols-5 gap-12 items-center">
      <Reveal className="md:col-span-2">
        <div className="relative aspect-[4/5] overflow-hidden max-w-[420px] mx-auto md:mx-0">
          <img src={images.founder} alt="Jean Lemoine, fondateur" className="w-full h-full object-cover object-top" />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#010c23]/95 via-[#010c23]/60 to-transparent p-6 pt-24">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#ff7300] mb-2">Fondateur</p>
            <p className="font-serif text-2xl text-white">Jean Lemoine</p>
            <p className="text-xs text-white/70 mt-2">Conseiller en Investissements Financiers — CIF · COA · COBSP</p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={120} className="md:col-span-3">
        <div>
          <div className="section-label mb-4"><span className="divider-gold"></span>Notre philosophie</div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#010c23] font-light leading-tight">
            Donner du sens à chaque décision patrimoniale
          </h2>
          <p className="mt-6 text-[#010c23]/75 leading-relaxed justify-text">
            Gérer un patrimoine ne consiste pas à accumuler des solutions. Il s’agit de faire des choix cohérents, au bon moment, avec une compréhension précise de leurs conséquences.
          </p>
          <p className="mt-4 text-[#010c23]/75 leading-relaxed justify-text">
            J’ai créé Ometis Patrimoine pour proposer un conseil exigeant, fondé sur la clarté, la pédagogie et la vision d’ensemble. Avant de recommander, nous cherchons à comprendre. Avant de mettre en œuvre, nous expliquons. Chaque décision doit pouvoir être comprise, assumée et replacée dans une stratégie globale.
          </p>
          <p className="mt-4 text-[#010c23]/75 leading-relaxed justify-text">
            Notre approche ne part jamais d’un produit. Elle part de votre situation, de vos priorités et de la trajectoire que vous souhaitez donner à votre patrimoine. Nous mettons en perspective les dimensions financières, fiscales et juridiques afin de construire une stratégie lisible, cohérente et durable.
          </p>
          <p className="mt-4 text-[#010c23]/75 leading-relaxed justify-text">
            Cette exigence se prolonge dans le temps. Le patrimoine évolue, tout comme les projets de vie, la famille et les marchés. Notre rôle est de rester présent, de préserver le cap et d’ajuster la stratégie avec méthode.
          </p>
          <p className="mt-4 text-[#010c23]/75 leading-relaxed justify-text">
            La véritable valeur du conseil réside alors dans cette capacité à rendre les décisions plus claires et l’avenir plus serein.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <span className="h-px w-10 bg-[#ff7300]" />
            <span className="font-serif italic text-lg text-[#010c23]">Jean Lemoine, fondateur d’Ometis Patrimoine</span>
            <a href={founderLinkedin} target="_blank" rel="noreferrer" aria-label="Profil LinkedIn de Jean Lemoine" className="inline-flex items-center gap-2 px-3 py-1.5 text-xs tracking-wider uppercase font-medium border border-black/15 text-[#010c23] hover:border-[#ff7300] hover:text-[#ff7300] transition-colors" data-testid="founder-linkedin">
              <Linkedin size={14} /> LinkedIn
            </a>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const ValuesSection = () => (
  <section className="py-28 bg-white">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <Reveal>
        <div className="section-label mb-4"><span className="divider-gold"></span>Nos valeurs</div>
        <h2 className="font-serif text-4xl md:text-5xl text-[#010c23] font-light max-w-3xl leading-tight">
          Les principes qui guident chacune de nos décisions
        </h2>
      </Reveal>
      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-5 gap-4 no-justify">
        {values.map((v, i) => (
          <Reveal key={v.title} delay={i * 60}>
            <div className="p-7 bg-[#fbf9f5] border-t-2 border-[#ff7300] h-full">
              <span className="font-serif text-4xl text-[#010c23]/20">{String(i + 1).padStart(2, '0')}</span>
              <h3 className="font-serif text-2xl text-[#010c23] mt-2 mb-4">{v.title}</h3>
              <p className="text-sm text-[#010c23]/70 leading-relaxed">{v.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const NameStorySection = () => (
  <section className="py-28 bg-[#010c23] text-white">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid md:grid-cols-12 gap-12 items-center">
      <Reveal className="md:col-span-5">
        <div className="section-label mb-4"><span className="divider-gold"></span>Histoire & nom</div>
        <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight">
          L’inspiration de la <span className="italic text-[#ff7300]">Mètis</span>
        </h2>
      </Reveal>
      <Reveal delay={120} className="md:col-span-7">
        <div className="space-y-5 text-white/80 leading-relaxed justify-text">
          <p>
            Le nom du cabinet, <span className="text-white font-medium">Ometis Patrimoine</span>, s’inspire de concepts riches de sens et en lien direct avec les valeurs et la mission du cabinet.
          </p>
          <p>
            La <em className="text-[#ff7300] not-italic font-medium">mètis</em> (grec ancien μῆτις) désigne une intelligence stratégique, reposant sur la ruse, la réflexion et l’adaptation immédiate aux circonstances changeantes.
          </p>
          <p>
            En numérologie, la lettre « O » est associée au chiffre 6, symbole de responsabilité, d’harmonie et de famille. Ces qualités sont au cœur de notre mission : bâtir une relation harmonieuse avec nos clients, tout en assumant la responsabilité de les accompagner.
          </p>
          <p>
            Cette philosophie inspire Ometis Patrimoine à adopter une approche flexible et stratégique, en se mettant « dans la peau du client » pour anticiper ses besoins patrimoniaux, financiers et fiscaux.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const CommitmentsSection = () => (
  <section className="py-28 bg-[#fbf9f5]">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <Reveal>
        <div className="section-label mb-4"><span className="divider-gold"></span>Nos engagements</div>
        <h2 className="font-serif text-4xl md:text-5xl text-[#010c23] font-light leading-tight max-w-3xl">
          Des engagements concrets, tenus dans la durée
        </h2>
      </Reveal>
      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-5 gap-4 no-justify">
        {engagements.map((e, i) => (
          <Reveal key={e.title} delay={i * 60}>
            <div className="p-6 bg-white border border-black/10 h-full">
              <div className="w-10 h-10 rounded-full bg-[#ff7300]/10 flex items-center justify-center text-[#ff7300] mb-4">
                <Check size={18} />
              </div>
              <h3 className="font-serif text-xl text-[#010c23]">{e.title}</h3>
              <p className="text-sm text-[#010c23]/70 mt-2">{e.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CtaSection = () => (
  <section className="py-24 bg-[#010c23] text-white text-center">
    <div className="max-w-3xl mx-auto px-6">
      <Reveal>
        <h2 className="font-serif text-4xl md:text-5xl font-light">Parlons de vos <span className="italic text-[#ff7300]">objectifs</span></h2>
        <p className="mt-6 text-white/70">Un premier échange pour comprendre votre situation et donner de la perspective à vos projets.</p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <a href={bookingUrl} target="_blank" rel="noreferrer" className="btn-primary"><Calendar size={18} /> Prendre rendez-vous en ligne</a>
          <Link to="/contact" className="btn-outline">Nous contacter <Mail size={16} /></Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const Cabinet = () => (
  <div>
    <HeroSection />
    <FounderSection />
    <ValuesSection />
    <NameStorySection />
    <CommitmentsSection />
    <CtaSection />
  </div>
);

export default Cabinet;
